<template>
  <DialogRoot :open="isOpen" @update:open="handleDismiss">
    <DialogPortal to="body">
      <DialogOverlay class="DialogOverlay" :style="{ 'z-index': overlayZIndex }" />

      <DialogContent
        class="WModal DialogContent modal"
        @keyup.Escape="handleDismiss"
        :class="{ responsive, expanded, mid, rounded, hideTitle }"
      >
        <div class="modal-card">
          <DialogTitle class="DialogTitle" v-if="!hideTitle">
            <slot name="header">
              <header v-if="hasTitle">
                <div></div>
                <div class="title__wrapper">
                  <w-title class="semi-bold" :size="4"><slot name="title" /></w-title>
                </div>
                <div></div>
              </header>
            </slot>
          </DialogTitle>
          <!-- required to suppress lib warning -->
          <DialogDescription />

          <DialogClose class="DialogClose" @click="handleClose" v-if="showClose">
            <slot name="close">
              <button class="close"></button>
            </slot>
          </DialogClose>

          <!-- section divider -->
          <hr v-if="(hasTitle || hasHeader) && hasBody" />

          <section class="modal-card-body" v-if="hasBody" :style="{ padding: noPadding ? '0' : '' }">
            <slot name="body" />
          </section>

          <!-- section divider -->
          <hr v-if="hasBody && hasFooter" />

          <footer class="modal-card-foot" v-if="hasFooter">
            <slot name="footer" />
          </footer>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<script>
  import { DialogPortal, DialogRoot, DialogContent, DialogOverlay, DialogTitle, DialogDescription, DialogClose } from 'radix-vue';

  export default {
    name: 'WModal',
    emits: ['close'],
    components: { DialogRoot, DialogPortal, DialogContent, DialogOverlay, DialogTitle, DialogDescription, DialogClose },
    props: {
      isOpen: Boolean,
      dismissible: { type: Boolean, default: false },
      showClose: { type: Boolean, default: true },
      responsive: { type: Boolean, default: true },
      expanded: Boolean,
      mid: { type: Boolean, default: false },
      overlayZIndex: { type: String, default: '' },
      noPadding: { type: Boolean, default: false },
      rounded: { type: Boolean, default: false },
      hideTitle: { type: Boolean, default: false },
    },
    methods: {
      handleClose() {
        this.$emit('close');
      },
      handleDismiss() {
        if (this.dismissible) this.$emit('close');
      },
    },
    computed: {
      hasHeader() {
        return !!this.$slots.header;
      },
      hasTitle() {
        return !!this.$slots.title;
      },
      hasBody() {
        return !!this.$slots.body;
      },
      hasFooter() {
        return !!this.$slots.footer;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/styles/variables';
  @import '@/assets/styles/mixins';

  .DialogOverlay {
    background-color: $neutral-black;
    filter: opacity(0.55);
    position: fixed;
    inset: 0;
    z-index: 9998;
  }

  .WModal {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: grid;
    place-items: center;

    &:not(.expanded) {
      .modal-card {
        max-width: 600px;
      }
    }

    &.expanded {
      .modal-card {
        min-width: 80vw;
        max-width: 1200px;
        max-height: 90vh;
        border-radius: 0;
      }

      .modal-card-body {
        padding: 2em 6em;
        background: #f7f7f7;
      }

      &:not(.hideTitle) {
        .DialogTitle {
          padding-block: 12px;
          padding-inline: 40px;
        }
      }
    }

    &.mid {
      .modal-card {
        min-width: 45vw;
        max-width: 800px;
        max-height: 90vh;
        border-radius: 0;
      }
    }

    &.rounded {
      .modal-card {
        border-radius: 1rem;
      }
    }
  }

  .modal-card {
    border-radius: 1rem;
    background-color: $white;

    .DialogClose {
      position: absolute;
      top: 0.625rem;
      right: 2rem;

      button.close {
        height: 2.5rem;
        width: 2.5rem;
      }

      &:focus {
        outline: none;
      }

      :focus-visible {
        outline: 2px dotted $input-border;
        border-radius: 0.5rem;
      }
    }

    header {
      padding: 1.25rem 2rem;
      position: relative;
      background-color: transparent;
    }

    hr {
      height: 1px;
    }

    .modal-card-body {
      background-color: transparent;
      padding: 2rem;

      &.center-content {
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        padding: 4.125rem 2rem;

        p {
          max-width: 415px;
        }
      }
    }

    .modal-card-foot {
      border: none;
      background-color: transparent;
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding: 1rem 2rem;
    }
  }

  .modal-card.expanded {
    display: flex;
    padding: 0.5rem;
    .modal-card {
      max-width: none;
      max-height: none;
      height: 100%;
      width: 100%;
    }
  }

  // .modal-card.mid {
  //   display: flex;
  //   padding: 0.5rem;
  //   .modal-card {
  //     max-width: none;
  //     max-height: none;
  //     height: 100%;
  //     width: 100%;
  //   }
  // }

  @include mobile {
    .WModal.responsive .modal-card {
      position: fixed;
      bottom: 0;
      border-radius: 1rem 1rem 0 0;
      max-height: 80vh;
      max-width: 100%;

      .DialogClose {
        top: 0.75rem;
        right: 0.75rem;

        button.close {
          height: 1.5rem;
          width: 1.5rem;
        }
      }

      header {
        padding: 0.75rem;
        display: grid;
        grid-template-columns: 2rem 1fr 2rem;

        .title__wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }

      .modal-card-body {
        padding: 1.5rem 1.5rem;
      }

      footer {
        padding: 0.75rem 1.5rem;
      }
    }
  }
</style>
